import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type { WorkflowTemplate } from '@nucleus-commerce/api-client/admin';
import { isApiError } from '@nucleus-commerce/api-client/transport';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Label } from '@nucleus-commerce/ui/components/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';

import { session } from '@/shared/session/session';

import { useDuplicateWorkflowTemplate } from '../hooks/use-workflow-template-mutations';

export interface DuplicateTemplateDialogProps {
  storeId: string;
  template: WorkflowTemplate | null;
  onOpenChange: (open: boolean) => void;
}

/** Copies a template into this store or another store of the organisation the person can act on. */
export function DuplicateTemplateDialog({
  storeId,
  template,
  onOpenChange,
}: DuplicateTemplateDialogProps) {
  const { t } = useTranslation(['workflows', 'common', 'errors']);
  const { me } = session.useSession();
  const duplicate = useDuplicateWorkflowTemplate(storeId);
  const message = useErrorMessage();
  const [targetStoreId, setTargetStoreId] = useState(storeId);
  const [name, setName] = useState('');
  const stores = me?.stores ?? [];
  const requestId = isApiError(duplicate.error) ? duplicate.error.requestId : undefined;
  const fieldError = isApiError(duplicate.error)
    ? duplicate.error.details.find((d) => d.field === 'body.name')?.message
    : undefined;

  const close = () => {
    duplicate.reset();
    setName('');
    setTargetStoreId(storeId);
    onOpenChange(false);
  };

  return (
    <Dialog open={template !== null} onOpenChange={(open) => (open ? onOpenChange(true) : close())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t('workflows:duplicate.title', { name: template?.name ?? '' })}
          </DialogTitle>
          <DialogDescription>{t('workflows:duplicate.description')}</DialogDescription>
        </DialogHeader>
        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (!template) return;
            duplicate.mutate(
              {
                id: template.id,
                body: {
                  ...(name.trim() ? { name: name.trim() } : {}),
                  ...(targetStoreId !== storeId ? { targetStoreId } : {}),
                },
              },
              { onSuccess: close },
            );
          }}
        >
          {duplicate.isError && !fieldError ? (
            <Alert variant="destructive" role="alert">
              <AlertDescription>
                {message(duplicate.error)}
                {requestId ? (
                  <span className="block text-xs">{t('errors:requestIdShort', { requestId })}</span>
                ) : null}
              </AlertDescription>
            </Alert>
          ) : null}
          <div className="flex flex-col gap-2">
            <Label htmlFor="duplicate-store">{t('workflows:duplicate.store')}</Label>
            <Select value={targetStoreId} onValueChange={setTargetStoreId}>
              <SelectTrigger id="duplicate-store" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {stores.map((store) => (
                  <SelectItem key={store.id} value={store.id}>
                    {store.id === storeId
                      ? t('workflows:duplicate.thisStore', { name: store.name })
                      : store.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="duplicate-name">{t('workflows:duplicate.name')}</Label>
            <Input
              id="duplicate-name"
              value={name}
              maxLength={120}
              placeholder={t('workflows:duplicate.namePlaceholder')}
              aria-invalid={fieldError ? true : undefined}
              onChange={(e) => setName(e.target.value)}
            />
            {fieldError ? <p className="text-destructive text-sm">{fieldError}</p> : null}
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={close}>
              {t('common:actions.cancel')}
            </Button>
            <Button type="submit" disabled={duplicate.isPending}>
              {duplicate.isPending
                ? t('workflows:duplicate.submitting')
                : t('workflows:duplicate.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
